import { Flex } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { Outlet, useNavigate, useParams } from "react-router-dom"

import { generateDocumentTitle } from "../../../../constants/document-title.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import type { RootState } from "../../../../store.ts"
import { reportsSetFilters } from "../../redux/reports.slice.ts"
import { fetchReports, toggleReportFavorite } from "../../redux/reports.thunks.ts"
import type { ReportListItem } from "../../types/types.ts"
import { ReportsRail, type ReportsNav } from "./ReportsRail.tsx"

export interface ReportsShellOutletContext {
  activeNav: ReportsNav
}

const ReportsShell = ({
  results,
  fetching,
  filters,
  setFilters,
  getReports,
  toggleFavorite
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: selectedId } = useParams<{ id: string }>()
  const [activeNav, setActiveNav] = useState<ReportsNav>("all")

  useEffect(() => {
    generateDocumentTitle(t("reports.title"))
  }, [t])

  useEffect(() => {
    getReports(filters)
  }, [getReports, filters])

  const handleSearch = useCallback(
    (search: string | null) => setFilters({ search }),
    [setFilters]
  )

  const handleNavChange = useCallback(
    (nav: ReportsNav) => {
      setActiveNav(nav)
      navigate(RoutesPaths.reports.list)
    },
    [navigate]
  )

  const handleSelect = useCallback(
    (report: ReportListItem) => navigate(RoutesPaths.reports.view(report.id)),
    [navigate]
  )

  const handleCreate = useCallback(
    () => navigate(RoutesPaths.reports.create),
    [navigate]
  )

  const handleToggleFavorite = useCallback(
    (report: ReportListItem) =>
      toggleFavorite({
        id: report.id,
        previousIsFavorite: report.is_favorite
      }),
    [toggleFavorite]
  )

  const context: ReportsShellOutletContext = { activeNav }

  return (
    <div className="grid size-full">
      <Flex
        gap="large"
        className="max-h-[calc(100vh-88px)] overflow-hidden p-1 -m-1"
      >
        <ReportsRail
          reports={results}
          loading={fetching}
          activeNav={activeNav}
          selectedId={selectedId}
          search={filters.search || ""}
          onNavChange={handleNavChange}
          onSearch={handleSearch}
          onSelect={handleSelect}
          onCreate={handleCreate}
          onToggleFavorite={handleToggleFavorite}
        />
        <div className="min-w-0 flex-1 overflow-auto">
          <Outlet context={context} />
        </div>
      </Flex>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  results: state.reports.results,
  fetching: state.reports.fetching,
  filters: state.reports.filters
})

const mapDispatchToProps = {
  setFilters: reportsSetFilters,
  getReports: fetchReports,
  toggleFavorite: toggleReportFavorite
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ReportsShell)
